import React from 'react';
import { Card, CardContent } from './ui/card';
import { Award, Clock, MapPin, Users, Shield, Star } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const AboutSection = () => {
  const { translate } = useLanguage();

  const stats = [
    { number: "12+", label: translate('yearsExperience'), icon: <Award className="w-6 h-6" /> },
    { number: "24/7", label: translate('serviceAvailable'), icon: <Clock className="w-6 h-6" /> },
    { number: "10000+", label: translate('satisfiedClients'), icon: <Star className="w-6 h-6" /> },
    { number: "100+", label: translate('destinations'), icon: <MapPin className="w-6 h-6" /> }
  ];

  const values = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: translate('securityConfidentiality'),
      description: translate('securityDescription')
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: translate('personalizedService'),
      description: translate('personalizedDescription')
    },
    {
      icon: <Star className="w-8 h-8" />,
      title: translate('excellencePunctuality'),
      description: translate('excellenceDescription')
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {translate('aboutTitle')}
          </h2>
          <div className="max-w-4xl mx-auto">
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              <span className="font-semibold text-yellow-600">Luxor Limousine SA</span> {translate('aboutDescription1')} <span className="font-semibold">{translate('aboutDescription2')}</span>.
            </p>
            
            <p className="text-lg text-gray-600 leading-relaxed">
              {translate('aboutDescription3')} <strong>{translate('aboutDescription4')}</strong>{translate('aboutDescription5')}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => (
            <Card key={index} className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="bg-yellow-500 text-black w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Values */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {values.map((value, index) => (
            <div key={index} className="text-center">
              <div className="bg-yellow-500 text-black w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                {value.icon}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{value.title}</h3>
              <p className="text-gray-600 leading-relaxed">{value.description}</p>
            </div>
          ))}
        </div>

        {/* Destinations */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            {translate('mainDestinations')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-yellow-500 text-lg font-semibold mb-2">{translate('airports')}</div>
              <div className="text-gray-600 space-y-1">
                <div>Genève-Cointrin</div>
                <div>Zurich-Kloten</div>
                <div>Bâle-Mulhouse</div>
                <div>Sion</div>
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-yellow-500 text-lg font-semibold mb-2">{translate('cities')}</div>
              <div className="text-gray-600 space-y-1">
                <div>Genève</div>
                <div>Lausanne</div>
                <div>Montreux</div>
                <div>Zurich</div>
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-yellow-500 text-lg font-semibold mb-2">{translate('skiResorts')}</div>
              <div className="text-gray-600 space-y-1">
                <div>Gstaad</div>
                <div>Verbier</div>
                <div>Crans-Montana</div>
                <div>Zermatt</div>
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-yellow-500 text-lg font-semibold mb-2">{translate('eventsDestinations')}</div>
              <div className="text-gray-600 space-y-1">
                <div>Hôtels de luxe</div>
                <div>Centres de congrès</div>
                <div>Festivals</div>
                <div>Mariages</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;